import { INVENTORY_COUNT_SLOT, inventorySlot } from '@rollcall/contracts';
import { byId, creationRule, speciesById } from './catalogue.js';
import { chosenContent, createStepOf } from './character.js';
/**
 * Инвентарь как свёртка журнала (D4).
 *
 * Снимка нет: список собирается из шага создания (стартовое
 * снаряжение развёрнуто из выбранного варианта) и из шагов, которыми
 * предметы брали, выбрасывали, надевали и снимали.
 *
 * Развёртывание идёт на каждом чтении, а не однажды при создании,
 * и это то же самое свойство, ради которого журнал и заведён:
 * поправили состав набора в записи класса — поправились все
 * персонажи, у которых этот набор выбран.
 *
 * Правил игры здесь ровно два, и оба из источника: доспех носят один
 * (второй поверх первого SRD не знает) и щит держат один — рука одна.
 * Всё остальное — учёт.
 */
/** Слот стартового снаряжения в шаге создания: `class.equipment`. */
const EQUIPMENT_SLOTS = ['class.equipment', 'background.equipment'];
function equipmentOf(item) {
    if (item === undefined)
        return null;
    if (item.type !== 'class' && item.type !== 'background')
        return null;
    return item.mechanics.startingEquipment;
}
/**
 * Стартовое снаряжение из шага создания.
 *
 * Вариант выбран игроком и записан в журнал буквой («A»), а состав
 * варианта лежит в записи класса или предыстории. Разворачивается
 * он здесь и сейчас — по той же причине, по которой лист не хранится.
 */
function startingEquipment(step, catalogue) {
    const held = [];
    let coinsGp = 0;
    for (const slot of EQUIPMENT_SLOTS) {
        const choice = step.payload.choices.find((item) => item.slot === slot);
        if (choice === undefined || typeof choice.value !== 'string')
            continue;
        const owner = slot.startsWith('class')
            ? chosenContent(step).class
            : chosenContent(step).background;
        const equipment = equipmentOf(owner === undefined ? undefined : byId(catalogue, owner));
        const option = equipment?.options.find((item) => item.option === choice.value);
        if (option === undefined)
            continue;
        coinsGp += option.goldGp;
        for (const line of option.items) {
            // Один и тот же предмет приходит из двух наборов — мантия есть
            // и у волшебника, и у мудреца, — и в инвентаре это одна строка
            // с количеством два, а не две одинаковые. Строки без записи
            // контента сходятся по названию: другого ключа у них нет.
            const same = held.find((item) => line.id === null
                ? item.id === null && item.name === line.name
                : item.id === line.id);
            if (same === undefined) {
                held.push({
                    id: line.id,
                    name: line.name,
                    quantity: line.quantity,
                    equipped: false,
                });
            }
            else {
                same.quantity += line.quantity;
            }
        }
    }
    return { held, coinsGp };
}
/**
 * Ссылки на предметы стартового снаряжения выбранного варианта.
 *
 * Нужны тому, кто поднимает контент для свёртки: в журнале записана
 * буква варианта, а не состав, и без этого списка предметы приехали бы
 * без веса, без вида и без эффектов — то есть кольчуга не считалась бы
 * кольчугой.
 */
export function startingEquipmentIds(journal, catalogue) {
    const step = journal.find((item) => item.kind === 'create' && item.stepIndex === 0);
    if (step === undefined)
        return [];
    return startingEquipment(step, catalogue)
        .held.map((item) => item.id)
        .filter((id) => id !== null);
}
/** Действие шага журнала: ключ выбора говорит, что сделали. */
function actionOf(step) {
    for (const kind of ['take', 'drop', 'set', 'equip', 'unequip']) {
        const choice = step.payload.choices.find((item) => item.slot === inventorySlot(kind));
        if (choice === undefined || typeof choice.value !== 'string')
            continue;
        const count = step.payload.choices.find((item) => item.slot === INVENTORY_COUNT_SLOT)?.value;
        return {
            kind,
            item: choice.value,
            ...(typeof count === 'number' ? { count } : {}),
        };
    }
    return null;
}
/** Применение одного действия к тому, что уже есть. */
function apply(held, action, catalogue) {
    const at = held.findIndex((item) => item.id === action.item);
    if (action.kind === 'take') {
        const count = action.count ?? 1;
        const existing = held[at];
        if (existing === undefined) {
            const record = byId(catalogue, action.item);
            held.push({
                id: action.item,
                name: record?.name ?? action.item,
                quantity: count,
                equipped: false,
            });
            return;
        }
        existing.quantity += count;
        return;
    }
    if (at === -1)
        return;
    if (action.kind === 'drop' || (action.kind === 'set' && (action.count ?? 0) === 0)) {
        held.splice(at, 1);
        return;
    }
    if (action.kind === 'set') {
        // Количество задаётся целиком, а не прибавкой: «у меня осталось
        // три стрелы» — это то, что человек видит, а не то, что он вычел.
        held[at] = { ...held[at], quantity: action.count ?? 1 };
        return;
    }
    held[at] = { ...held[at], equipped: action.kind === 'equip' };
}
/** Механика записи предмета; у прочих записей её нет. */
function itemMechanics(record) {
    return record?.type === 'item' ? record.mechanics : null;
}
function weightOf(mechanics) {
    if (mechanics === null)
        return null;
    // У доспеха, щита и оружия вес есть всегда; у снаряжения
    // и магических предметов бывает пустым — у набора для игр
    // и музыкального инструмента он зависит от варианта (REPORT,
    // раздел 3). Ноль там был бы враньём, поэтому «неизвестно».
    return 'weightLb' in mechanics ? (mechanics.weightLb ?? null) : null;
}
function categoryOf(mechanics) {
    return mechanics === null ? null : mechanics.category;
}
/** Надеть можно доспех, щит и оружие; мешок надеть нельзя. */
export function isEquippable(category) {
    return category === 'armor' || category === 'shield' || category === 'weapon';
}
/** Строка таблицы грузоподъёмности по размеру существа. */
function capacityFor(capacity, size) {
    return capacity.bySize.find((row) => row.size === size);
}
/**
 * Инвентарь персонажа: что есть, что надето, сколько весит.
 *
 * Размер берётся у выбранного вида. У всех видов SRD он `small`
 * либо `medium`, а множитель у них в таблице один и тот же, поэтому
 * выбор размера у человека и тифлинга (их два) на число пока
 * не влияет; когда выбор появится слотом, брать его будем отсюда же.
 */
export function characterInventory({ journal, catalogue, strength, }) {
    const step = createStepOf(journal);
    const { held, coinsGp } = startingEquipment(step, catalogue);
    const steps = [...journal].sort((left, right) => left.stepIndex - right.stepIndex);
    for (const item of steps) {
        if (item.kind !== 'inventory')
            continue;
        const action = actionOf(item);
        if (action !== null)
            apply(held, action, catalogue);
    }
    const entries = held.map((item) => {
        const mechanics = itemMechanics(item.id === null ? undefined : byId(catalogue, item.id));
        const record = item.id === null ? undefined : byId(catalogue, item.id);
        return {
            id: item.id,
            // Имя записи важнее имени из набора: «Daggers» в наборе — это
            // запись «Dagger», и количество стоит рядом отдельным числом.
            name: record?.name ?? item.name,
            quantity: item.quantity,
            equipped: item.equipped,
            category: categoryOf(mechanics),
            weightLb: weightOf(mechanics),
            text: record?.text ?? null,
            textRu: record?.translation?.ru.text ?? null,
        };
    });
    const rule = creationRule(catalogue);
    const size = sizeOf(step, catalogue);
    const row = rule === undefined ? undefined : capacityFor(rule.mechanics.carryingCapacity, size);
    const totalWeightLb = entries.reduce((sum, entry) => sum + (entry.weightLb ?? 0) * entry.quantity, 0);
    const carryLb = (row?.carryPerStrength ?? 0) * strength;
    const dragLb = (row?.dragPerStrength ?? 0) * strength;
    return {
        entries,
        coinsGp,
        // Дробные фунты в SRD есть («1/2 lb.»), и складывать их в double
        // без округления — способ получить 3.0000000000000004.
        totalWeightLb: Math.round(totalWeightLb * 100) / 100,
        carryLb,
        dragLb,
        overCarry: carryLb > 0 && totalWeightLb > carryLb,
        weightUnknown: entries
            .filter((entry) => entry.weightLb === null)
            .map((entry) => entry.name),
    };
}
/** Размер персонажа: первый из тех, что предлагает вид. */
function sizeOf(step, catalogue) {
    const species = chosenContent(step).species;
    if (species === undefined)
        return 'medium';
    const record = speciesById(catalogue, species);
    return record?.mechanics.sizes[0] ?? 'medium';
}
/** Что надето: то, из чего собирается `state.equipped` контекста. */
export function equippedItems(input) {
    return equippedOf(characterInventory(input));
}
/** То же по готовому инвентарю: считать свёртку дважды незачем. */
export function equippedOf(inventory) {
    return inventory.entries
        .filter((entry) => entry.equipped && entry.id !== null)
        .map((entry) => entry.id);
}
/**
 * Груз для контекста расчёта: вес при себе и предел переносимого.
 *
 * Отсюда движок узнаёт о перегрузе — и только отсюда: сам он веса
 * не считает, потому что вес складывается из инвентаря, а инвентарь
 * собирается свёрткой журнала до всякого расчёта величин.
 */
export function carryState(inventory) {
    return {
        weightLb: inventory.totalWeightLb,
        capacityLb: inventory.carryLb,
        over: inventory.overCarry,
    };
}
/**
 * Можно ли выполнить действие. Пустой список — можно.
 *
 * Проверка здесь, а не на сервере, по той же причине, что и всё
 * остальное правило: список доступного и проверка присланного
 * обязаны быть одним кодом (CLAUDE.md, п. 5).
 */
export function validateInventoryAction(input, action) {
    const inventory = characterInventory(input);
    const entry = inventory.entries.find((item) => item.id === action.item);
    const record = byId(input.catalogue, action.item);
    const refuse = (code, message, blockedBy = null) => [{ code, item: action.item, blockedBy, message }];
    if (record === undefined) {
        return refuse('unknown-item', `записи ${action.item} среди контента нет`);
    }
    if (record.type !== 'item') {
        return refuse('not-a-record', `${action.item} — не предмет снаряжения`);
    }
    if (action.kind === 'take') {
        return (action.count ?? 1) > 0
            ? []
            : refuse('bad-count', 'взять можно хотя бы одну штуку');
    }
    if (entry === undefined) {
        return refuse('not-in-inventory', `предмета ${action.item} у персонажа нет`);
    }
    if (action.kind === 'set') {
        return (action.count ?? -1) >= 0
            ? []
            : refuse('bad-count', 'количество не бывает отрицательным');
    }
    if (action.kind === 'drop')
        return [];
    if (action.kind === 'unequip') {
        return entry.equipped ? [] : refuse('not-equipped', `${action.item} не надет`);
    }
    // Остаётся `equip`.
    if (entry.equipped) {
        return refuse('already-equipped', `${action.item} уже надет`);
    }
    if (!isEquippable(entry.category)) {
        return refuse('not-equippable', `${action.item} не надевают`);
    }
    const worn = inventory.entries.find((item) => item.equipped && item.category === entry.category);
    if (worn !== undefined && entry.category === 'armor') {
        return refuse('armor-already-worn', `на персонаже уже надет доспех ${worn.id ?? worn.name}`, worn.id);
    }
    if (worn !== undefined && entry.category === 'shield') {
        return refuse('shield-already-held', `щит ${worn.id ?? worn.name} уже в руке`, worn.id);
    }
    return [];
}
/** Шаг журнала для действия: то, что запишется в журнал (D4). */
export function buildInventoryStep(stepIndex, action) {
    const choices = [{ slot: inventorySlot(action.kind), value: action.item }];
    if (action.count !== undefined) {
        choices.push({ slot: INVENTORY_COUNT_SLOT, value: action.count });
    }
    return { stepIndex, kind: 'inventory', payload: { choices, effects: [] } };
}
//# sourceMappingURL=inventory.js.map